import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { UploadRejectedDetails } from "../../hooks/useFileUpload";

export interface UploadRejectedDialogProps {
  file: UploadRejectedDetails | null;
  onClose: () => void;
}

/**
 * ADR 0011 section 5: a rejected upload is refused synchronously, and
 * nothing is stored anywhere the portal, a partner or staff can ever see -
 * there is no row, chip or listing for it any more. This dialog is the
 * only place the outcome is shown, so it has to say plainly that the file
 * was not delivered: the file name, the server's fixed message for the
 * reason (always ends "Nothing was stored."), and the reference to quote
 * when contacting Leeft. Never a signature, an S3 key or any other
 * scanner-internal detail: the server never sends one.
 */
export function UploadRejectedDialog({ file, onClose }: UploadRejectedDialogProps) {
  return (
    <Dialog open={file !== null} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="upload-rejected-title">
      <DialogTitle id="upload-rejected-title">File not uploaded</DialogTitle>
      <DialogContent>
        {file && (
          <Stack spacing={2}>
            <DialogContentText>{file.message}</DialogContentText>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>File:</strong> {file.name}
              </Typography>
              <Typography variant="body2">
                <strong>Reference:</strong> {file.reference}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Quote this reference if you contact Leeft about this file.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
